import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import PptxGenJS from 'pptxgenjs';

import { waitForElementDimensions } from './wait-for-element-dimensions';

// Letter landscape dimensions in inches
const LETTER_WIDTH_IN = 11;
const LETTER_HEIGHT_IN = 8.5;

const CAPTURE_SCALE = 2;

// Renders are re-encoded at the size they actually print: a 120px-wide header thumbnail
// needs ~250px, not the full resolution of the source blob.
const RENDER_JPEG_QUALITY = 0.86;
const RENDER_TARGET_DPI = 200;

export type ExportBlueprintParams = {
	container: HTMLDivElement;
	fileName: string;
	onProgress?: (current: number, total: number) => void;
};

const BASELINE_PROBE_RULE = 'body > div > img[width="1"][height="1"][style*="vertical-align"] { display: inline !important; }';

/**
 * html2canvas reads the font baseline from a 1x1 probe image it aligns to a text span's baseline,
 * which only works while that probe is inline. Chakra's global `img { display: block }` reset drops
 * the probe onto its own line, so every rasterized baseline lands 8px low — visible as text sitting
 * below centre in any table cell. Restores the probe to inline for the duration of a capture.
 */
const withBaselineProbeInline = async <T>(capture: () => Promise<T>): Promise<T> => {
	const style = document.createElement('style');
	style.textContent = BASELINE_PROBE_RULE;
	document.head.appendChild(style);

	try {
		return await capture();
	} finally {
		style.remove();
	}
};

export type CaptureProgressCallback = (current: number, total: number) => void;

/**
 * html2canvas clones the whole document for every capture, so with all pages force-mounted
 * during an export each of the N captures walks all N pages — quadratic, and the dominant
 * cost of the export. Pruning the other page subtrees from the clone makes it linear.
 */
const isOtherBlueprintPage = (pageElement: HTMLElement): ((element: Element) => boolean) => (element: Element): boolean =>
	element !== pageElement && element.hasAttribute('data-blueprint-page');

// Frees the backing bitmap once encoded — holding every page's 2200x1700 canvas is ~200MB.
const releaseCanvas = (canvas: HTMLCanvasElement): void => {
	canvas.width = 0;
	canvas.height = 0;
};

type RenderPlacement = {
	data: string;
	x: number;
	y: number;
	w: number;
	h: number;
	sizing: { type: 'contain'; w: number; h: number };
};

type PlacedRender = {
	img: HTMLImageElement;
	placement: RenderPlacement;
};

/**
 * JPEG re-encode at the size the render actually prints. The source blobs are already
 * JPEG photographs, so the previous full-resolution PNG round-trip inflated each one by
 * roughly an order of magnitude to fill a thumbnail-sized slot. Re-encoding (rather than
 * passing the source through) is still required: pptxgenjs embeds webp data URLs broken
 * outside M365 and writes raw https URLs as corrupt image bytes.
 */
const renderImgToJpegDataUrl = async (img: HTMLImageElement, printedWidthIn: number, printedHeightIn: number): Promise<string | null> => {
	try {
		await img.decode();
		const { naturalWidth, naturalHeight } = img;
		if (naturalWidth === 0 || naturalHeight === 0) return null;

		const maxWidth = Math.max(1, Math.round(printedWidthIn * RENDER_TARGET_DPI));
		const maxHeight = Math.max(1, Math.round(printedHeightIn * RENDER_TARGET_DPI));
		const scale = Math.min(1, maxWidth / naturalWidth, maxHeight / naturalHeight);

		const canvas = document.createElement('canvas');
		canvas.width = Math.max(1, Math.round(naturalWidth * scale));
		canvas.height = Math.max(1, Math.round(naturalHeight * scale));
		const ctx = canvas.getContext('2d');
		if (!ctx) return null;
		// JPEG has no alpha — fill first so any transparent render edge lands on white, not black.
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
		return canvas.toDataURL('image/jpeg', RENDER_JPEG_QUALITY);
	} catch {
		return null;
	}
};

const getRenderPlacements = async (pageElement: HTMLElement): Promise<Array<PlacedRender>> => {
	const pageRect = pageElement.getBoundingClientRect();
	const renders = pageElement.querySelectorAll<HTMLImageElement>('[data-blueprint-render]');
	const placed: Array<PlacedRender> = [];

	for (const img of Array.from(renders)) {
		if (img.src === '') continue;

		const rect = img.getBoundingClientRect();
		const w = (rect.width / pageRect.width) * LETTER_WIDTH_IN;
		const h = (rect.height / pageRect.height) * LETTER_HEIGHT_IN;

		const data = await renderImgToJpegDataUrl(img, w, h);
		if (data === null) continue;

		placed.push({
			img,
			placement: {
				data,
				x: ((rect.left - pageRect.left) / pageRect.width) * LETTER_WIDTH_IN,
				y: ((rect.top - pageRect.top) / pageRect.height) * LETTER_HEIGHT_IN,
				w,
				h,
				sizing: { type: 'contain', w, h }
			}
		});
	}

	return placed;
};

/**
 * Captures a single page with its 3D render(s) masked out, returning the background
 * bitmap plus the separate render placements so they stay swappable in the slide.
 */
const capturePageWithSeparateRenders = async (pageElement: HTMLElement): Promise<{ background: HTMLCanvasElement; renders: Array<RenderPlacement> }> => {
	// html2canvas can't locate a zero-sized page in its clone; block until this page has laid out.
	await waitForElementDimensions({ element: pageElement });

	const placed = await getRenderPlacements(pageElement);
	// Only mask renders that produced a valid PNG; failed ones stay baked into the background.
	const renderElements = placed.map(({ img }) => img);

	// Hide via visibility (not data-html2canvas-ignore, which drops the element from the clone and reflows the
	// flex layout) so the render's box stays reserved and surrounding text/boxes keep their on-screen position.
	const previousVisibilities = renderElements.map((img) => img.style.visibility);
	renderElements.forEach((img) => { img.style.visibility = 'hidden'; });

	try {
		const background = await withBaselineProbeInline(() => html2canvas(pageElement, {
			scale: CAPTURE_SCALE,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#FFFFFF',
			logging: false,
			ignoreElements: isOtherBlueprintPage(pageElement)
		}));
		return { background, renders: placed.map(({ placement }) => placement) };
	} finally {
		renderElements.forEach((img, i) => { img.style.visibility = previousVisibilities[i]; });
	}
};

/**
 * Exports the blueprint view to a PDF file (Letter landscape, 11" x 8.5").
 * Each `[data-blueprint-page]` element becomes one page. `compress` is required: without
 * it jsPDF embeds every page as a raw unfiltered RGB bitmap (2200x1700x3 ≈ 10.7MB/page),
 * which is what pushed real exports past 160MB. Renders ride along as separate JPEGs so
 * the line art stays lossless.
 */
export const exportBlueprintToPdf = async (params: ExportBlueprintParams): Promise<void> => {
	const { container, fileName, onProgress } = params;
	const pageElements = container.querySelectorAll<HTMLElement>('[data-blueprint-page]');
	const total = pageElements.length;

	if (total === 0) return;

	const pdf = new jsPDF({
		orientation: 'landscape',
		unit: 'in',
		format: 'letter',
		compress: true
	});

	// The 3D thumbnail repeats in every page header; a stable alias per unique render
	// makes jsPDF embed those bytes once instead of once per page.
	const renderAliases = new Map<string, string>();

	for (let i = 0; i < total; i++) {
		onProgress?.(i, total);

		if (i > 0) pdf.addPage('letter', 'landscape');

		const { background, renders } = await capturePageWithSeparateRenders(pageElements[i]);
		pdf.addImage(background.toDataURL('image/png'), 'PNG', 0, 0, LETTER_WIDTH_IN, LETTER_HEIGHT_IN, undefined, 'FAST');
		releaseCanvas(background);

		for (const render of renders) {
			const alias = renderAliases.get(render.data) ?? `render-${renderAliases.size}`;
			renderAliases.set(render.data, alias);
			pdf.addImage(render.data, 'JPEG', render.x, render.y, render.w, render.h, alias, 'FAST');
		}
	}

	onProgress?.(total, total);
	pdf.save(`${fileName}.pdf`);
};

/**
 * Exports the blueprint view to a PowerPoint file (widescreen landscape).
 * Each `[data-blueprint-page]` element becomes one slide. The page bitmap is the
 * background and each 3D render is added as a separate positioned image so it can be
 * swapped without editing the baked-in page image.
 */
export const exportBlueprintToPptx = async (params: ExportBlueprintParams): Promise<void> => {
	const { container, fileName, onProgress } = params;
	const pageElements = container.querySelectorAll<HTMLElement>('[data-blueprint-page]');
	const total = pageElements.length;

	if (total === 0) return;

	const pptx = new PptxGenJS();
	pptx.defineLayout({ name: 'LETTER_LANDSCAPE', width: LETTER_WIDTH_IN, height: LETTER_HEIGHT_IN });
	pptx.layout = 'LETTER_LANDSCAPE';

	for (let i = 0; i < total; i++) {
		onProgress?.(i, total);

		const { background, renders } = await capturePageWithSeparateRenders(pageElements[i]);
		const slide = pptx.addSlide();

		slide.addImage({
			data: background.toDataURL('image/png'),
			x: 0,
			y: 0,
			w: LETTER_WIDTH_IN,
			h: LETTER_HEIGHT_IN
		});
		releaseCanvas(background);

		for (const render of renders) {
			slide.addImage(render);
		}
	}

	onProgress?.(total, total);
	await pptx.writeFile({ fileName: `${fileName}.pptx` });
};
